/**
 * Create and initialise Tako pagination
 */

/* -----------------------------------------------------------------------------
 * Tako Pagination
 * -------------------------------------------------------------------------- */
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';
import { showElement } from './utils/showElement';
import { hideElement } from './utils/hideElement';

const COMPONENT_NAME = 'Pagination';

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allTakoPaginations = scope.querySelectorAll('.tako-pagination');

    allTakoPaginations.forEach((pagination) => {
        try {
            // ToDo: Override direction and set to left-to-right until support for right-to-left is implemented
            pagination.setAttribute('dir', 'ltr');

            layoutPageButtons(pagination);
            addPaginationEventListeners(
                pagination.querySelector('.tako-pagination__pages'),
            );
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: pagination,
            });
        }
    });
};

const layoutPageButtons = (pagination) => {
    const pageContainer = pagination.querySelector('.tako-pagination__pages');

    if (!pageContainer) {
        return;
    }

    const createEllipsisIcon = () => {
        const ellipsisIcon = document.createElement('span');
        ellipsisIcon.classList.add(
            'tako-icon',
            'tako-icon--primary',
            'tako-pagination__ellipsis',
        );
        ellipsisIcon.innerHTML = '<i class="fas fa-ellipsis"></i>';

        return ellipsisIcon;
    };

    const pages = Array.from(
        pageContainer.querySelectorAll('.tako-pagination__page-item'),
    );
    const prevArrow = pageContainer.querySelector(
        '.tako-button--icon .fa-angle-left',
    );
    const nextArrow = pageContainer.querySelector(
        '.tako-button--icon .fa-angle-right',
    );
    const numOfPages = pages.length;
    const currentPage = pageContainer.querySelector(
        '.tako-pagination__page-item.active',
    );
    const currentPageIndex = currentPage ? pages.indexOf(currentPage) : 0;

    // Check whether to disable prev/next buttons
    if (prevArrow) {
        if (currentPageIndex <= 0) {
            prevArrow.parentNode.classList.add('disabled');
        } else {
            prevArrow.parentNode.classList.remove('disabled');
        }
    }

    if (nextArrow) {
        if (currentPageIndex >= numOfPages - 1) {
            nextArrow.parentNode.classList.add('disabled');
        } else {
            nextArrow.parentNode.classList.remove('disabled');
        }
    }

    if (!currentPage && !numOfPages) {
        return;
    }

    if (!currentPage && numOfPages >= 0) {
        pages[currentPageIndex].classList.add('active');
    }

    // Add class to existing ellipsis icons
    const existingEllipsisIcons =
        pageContainer.querySelectorAll('svg.fa-ellipsis');
    existingEllipsisIcons.forEach((icon) => {
        icon.parentNode.addClass('tako-pagination__ellipsis');
    });

    // Remove previous ellipsis icons
    pageContainer
        .querySelectorAll('.tako-pagination__ellipsis')
        .forEach((icon) => {
            icon.remove();
        });

    if (numOfPages < 5) {
        return;
    }

    // Show and hide page numbers and ellipsis icons
    if (currentPageIndex < 4) {
        pageContainer.insertBefore(createEllipsisIcon(), pages[numOfPages - 1]);
        pages.forEach((el, index) => {
            if (index > 4 && index !== numOfPages - 1) {
                hideElement(el);
            } else {
                showElement(el);
            }
        });
    } else if (currentPageIndex >= numOfPages - 4) {
        pages[0].after(createEllipsisIcon());
        pages.forEach((el, index) => {
            if (index < numOfPages - 5 && index !== 0) {
                hideElement(el);
            } else {
                showElement(el);
            }
        });
    } else if (currentPageIndex >= 4 && currentPageIndex <= numOfPages - 4) {
        pages[0].insertAdjacentElement('afterend', createEllipsisIcon());
        pageContainer.insertBefore(createEllipsisIcon(), pages[numOfPages - 1]);
        pages.forEach((el, index) => {
            if (
                index === currentPageIndex ||
                index === currentPageIndex + 1 ||
                index === currentPageIndex - 1 ||
                index === 0 ||
                index === numOfPages - 1
            ) {
                showElement(el);
            } else {
                hideElement(el);
            }
        });
    }
};

const addPaginationEventListeners = (el) => {
    if (!el) {
        return;
    }

    const takoPagination = el.parentNode;
    const prevArrow = el.querySelector('.tako-button--icon .fa-angle-left');
    const nextArrow = el.querySelector('.tako-button--icon .fa-angle-right');
    const pageNumberButtons = el.querySelectorAll(
        '.tako-pagination__page-item',
    );

    // Select specific page
    pageNumberButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            button.parentNode
                .querySelector('.active')
                .classList.remove('active');
            button.classList.add('active');
            layoutPageButtons(takoPagination);
        });
    });

    // Select previous page
    if (prevArrow) {
        prevArrow.parentNode.addEventListener('click', (event) => {
            event.preventDefault();
            const currentElement =
                event.target.parentNode.querySelector('.active');
            const prevElement = currentElement.previousElementSibling;

            if (
                prevElement &&
                prevElement.classList.contains('tako-pagination__page-item')
            ) {
                prevElement.classList.add('active');
                currentElement.classList.remove('active');
                layoutPageButtons(takoPagination);
            } else {
                return;
            }
        });
    }

    // Select next page
    if (nextArrow) {
        nextArrow.parentNode.addEventListener('click', (event) => {
            event.preventDefault();
            const currentElement =
                event.target.parentNode.querySelector('.active');
            const nextElement = currentElement.nextElementSibling;

            if (
                nextElement &&
                nextElement.classList.contains('tako-pagination__page-item')
            ) {
                nextElement.classList.add('active');
                currentElement.classList.remove('active');
                layoutPageButtons(takoPagination);
            } else {
                return;
            }
        });
    }
};
